import React from 'react';
import { BUSINESS, REFUND, GRIEVANCE } from './businessInfo';

// Refund & Cancellation. The page gateway reviewers read most closely, because
// it is the one that decides chargeback disputes.
//
// The windows and percentages come from REFUND in businessInfo.js. They are
// PROPOSALS — a commitment the restaurant is held to, so the client must confirm
// them before this goes live.
//
// One <section> per heading: spacing on a phone, cards of a grid on a computer.

const RefundPolicy = () => (
    <div className="so-infopage">
        <p className="so-infopage__meta">
            Last updated: {BUSINESS.lastUpdated}
        </p>

        <p className="so-infopage__lead">
            A special order is cooked for the date you choose, and ingredients for it
            are bought ahead of that date. How much we can refund therefore depends on
            how close to that date you cancel.
        </p>

        <section className="so-infopage__section">
            <h3>Cancelling a special order</h3>
            <ul>
                <li>
                    <strong>More than {REFUND.freeCancelHours} hours before</strong> the
                    requested date and time — cancel for a full refund of the amount paid.
                </li>
                <li>
                    <strong>Between {REFUND.partialCancelHours} and {REFUND.freeCancelHours} hours
                    before</strong> — {REFUND.partialPercent}% of the amount paid is refunded,
                    because ingredients have already been bought for your order.
                </li>
                <li>
                    <strong>Less than {REFUND.partialCancelHours} hours before</strong> — the
                    order is already being prepared and cannot be refunded.
                </li>
            </ul>
            <p>
                To cancel, call us on the number on the Contact us page and quote your order
                number. Please do not rely on a message to cancel an order that is close to
                its time; a call is confirmed immediately.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>If we cannot fulfil your order</h3>
            <p>
                If we cannot supply what you ordered — an item is unavailable, or the kitchen
                cannot deliver on the date — we will tell you as soon as we know and refund
                the affected amount in full, whatever the timing. You can also choose to move
                the order to another date instead of taking the refund.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>If something was wrong with the order</h3>
            <p>
                Tell us on the same day, before the food is consumed, and keep the items as
                they arrived. We will replace the affected items or refund them. Decisions on
                a refund for quality are made case by case after we have spoken to you — we
                do not refund an order that was fully consumed.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>Failed, duplicate or double payments</h3>
            <p>
                If a payment fails but money has left your account, or you were charged
                twice for the same order, it is refunded in full. Bank-side reversals usually
                arrive within {REFUND.failedPaymentDays}; if a failed payment has not come
                back after that, contact us with the date, amount and payment reference and we
                will trace it with the payment gateway.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>How refunds are paid</h3>
            <ul>
                <li>
                    Refunds go back to the <strong>original payment method</strong> — the card,
                    UPI ID or account the payment came from. We cannot redirect a refund to a
                    different account, and we do not refund online payments in cash.
                </li>
                <li>
                    Once approved, the refund is initiated within 24 hours and normally reaches
                    you in <strong>{REFUND.refundDays}</strong>, depending on your bank.
                </li>
                <li>
                    Any gateway or bank charge levied on the original payment is refunded along
                    with it.
                </li>
            </ul>
        </section>

        <section className="so-infopage__section">
            <h3>Pickup orders not collected</h3>
            <p>
                A pickup order is held for you for a reasonable period after the time you
                chose. Food that is never collected cannot be refunded — call us if you are
                running late and we will do what we can.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>Still not resolved?</h3>
            <p>
                {GRIEVANCE.name
                    ? <>
                        Escalate to our grievance officer, {GRIEVANCE.name}
                        {GRIEVANCE.email ? ' (' + GRIEVANCE.email + ')' : ''}. We aim to
                        respond within {GRIEVANCE.responseHours} hours.
                    </>
                    : <>
                        Call the restaurant on {BUSINESS.phone} and ask for the manager. We
                        aim to settle a refund question within {GRIEVANCE.responseHours} hours
                        of it reaching us.
                    </>}
            </p>
        </section>
    </div>
);

export default RefundPolicy;
